import {
    useState, useEffect
} from 'react';

export enum ScriptState {
    idle = 'idle',
    loading = 'loading',
    ready = 'ready',
    error = 'error'
}

export default function useScript (src: string) {
    // Keep track of script status ("idle", "loading", "ready", "error")
    const [status, setStatus] = useState<string | null>(src ? ScriptState.loading : ScriptState.idle);

    useEffect(
        () => {
            // Allow falsy src value if waiting on other data needed for
            // constructing the script URL passed to this hook.
            if (!src) {
                setStatus(ScriptState.idle);

                return;
            }

            // Fetch existing script element by src
            // It may have been added by another intance of this hook
            // eslint-disable-next-line no-undef
            let script = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);

            if (!script) {
                // Create script
                // eslint-disable-next-line no-undef
                script = document.createElement('script');
                script.src = src;
                script.async = true;
                script.setAttribute('data-status', ScriptState.loading);
                // Add script to document body
                // eslint-disable-next-line no-undef
                document.body.appendChild(script);

                // Store status in attribute on script
                // This can be read by other instances of this hook
                const setAttributeFromEvent = (event: any) => {
                    if (script) {
                        script.setAttribute(
                            'data-status',
                            event.type === 'load' ? ScriptState.ready : ScriptState.error
                        );
                    }
                };

                script.addEventListener('load', setAttributeFromEvent);
                script.addEventListener('error', setAttributeFromEvent);
            } else {
                // Grab existing script status from attribute and set to state.
                setStatus(script.getAttribute('data-status'));
            }

            // Script event handler to update status in state
            // Note: Even if the script already exists we still need to add
            // event handlers to update the state for *this* hook instance.
            const setStateFromEvent = (event: any) => {
                setStatus(event.type === 'load' ? ScriptState.ready : ScriptState.error);
            };

            // Add event listeners
            script.addEventListener('load', setStateFromEvent);
            script.addEventListener('error', setStateFromEvent);

            // Remove event listeners on cleanup
            // eslint-disable-next-line consistent-return
            return () => {
                if (script) {
                    script.removeEventListener('load', setStateFromEvent);
                    script.removeEventListener('error', setStateFromEvent);
                }
            };
        },
        // Only re-run effect if script src changes
        [src]
    );

    return status;
}
